import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { api } from '@/lib/api';
import { Sparkle, Eye, Heart, Compass, Crown, ShoppingBag, CheckCircle } from '@phosphor-icons/react';

const TIER_META = {
  burst:  { label: 'Mood Bursts',   sub: '$1 · 72 hours',     icon: Sparkle, color: 'text-bx-cyan' },
  voice:  { label: 'Whispers',       sub: '$3 · per bubble',   icon: Heart,   color: 'text-bx-magenta' },
  eyes:   { label: 'Rare Eyes',      sub: '$5 · per bubble',   icon: Eye,     color: 'text-bx-volt' },
  skin:   { label: 'Skins',          sub: '$8 · permanent',    icon: Compass, color: 'text-bx-gold' },
  origin: { label: 'Bubble Origins', sub: '$10 · birth-fact',  icon: Crown,   color: 'text-bx-legendary' },
};

const ORDER = ['burst', 'voice', 'eyes', 'skin', 'origin'];

// tiny preview swatch that mirrors the actual aura/skin/eyes CSS
const Preview = ({ item }) => {
  if (item.kind === 'mood_burst') {
    return <div className={`relative h-20 w-20 rounded-full overflow-hidden bg-black/40 mx-auto aura-wrap`}>
      <div className={`aura-layer aura-${item.css}`} />
      <div className="absolute inset-3 rounded-full bg-gradient-to-br from-bx-cyan/30 to-bx-magenta/30 z-10" />
    </div>;
  }
  if (item.kind === 'skin') {
    return <div className="relative h-20 w-20 rounded-full mx-auto"
      style={{
        background: `conic-gradient(from 0deg, ${item.palette[0]}, ${item.palette[1]}, ${item.palette[2]}, ${item.palette[0]})`,
        boxShadow: `0 0 24px ${item.palette[1]}88`,
      }} />;
  }
  if (item.kind === 'eyes') {
    const key = item.id?.split('.')[1] || '';
    return <div className="relative h-20 w-20 rounded-full bg-black/60 mx-auto overflow-hidden">
      <div className={`eyes-overlay eyes-${key}`} style={{inset:'30% 18% auto 18%', height:'30%'}}>
        <span /><span />
      </div>
    </div>;
  }
  if (item.kind === 'whisper') {
    return <div className="h-20 w-20 mx-auto rounded-full grid place-items-center text-3xl bg-gradient-to-br from-black via-bx-magenta/30 to-black">🎙</div>;
  }
  if (item.kind === 'origin') {
    return <div className="h-20 w-20 mx-auto rounded-full grid place-items-center text-2xl font-display font-black bg-gradient-to-br from-black via-bx-gold/30 to-black text-bx-gold">★</div>;
  }
  return null;
};

const Shop = () => {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [catalog, setCatalog] = useState([]);
  const [mine, setMine] = useState([]);
  const [foundersPass, setFoundersPass] = useState(false);
  const [bubbles, setBubbles] = useState([]);
  const [pickBubble, setPickBubble] = useState({ open: false, item: null });
  const [buying, setBuying] = useState(null);

  // Fulfilment polling on Stripe return
  useEffect(() => {
    const sid = params.get('shop_session') || params.get('spark_session')
              || params.get('fp_session');
    if (!sid) return undefined;
    toast.message('Verifying purchase…');
    let cancel = false;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        // Reload my cosmetics — backend webhook will have credited it
        const { data } = await api.get('/me/cosmetics');
        setMine(data.cosmetics || []);
        if ((data.cosmetics || []).length > 0 || tries > 12) {
          toast.success('Your decoration arrived ✦');
          return;
        }
      } catch { /* ignore */ }
      if (!cancel) setTimeout(poll, 1800);
    };
    poll();
    return () => { cancel = true; };
  }, [params]);

  const loadAll = async () => {
    try {
      const [c, m, b] = await Promise.all([
        api.get('/shop/catalog'),
        api.get('/me/cosmetics'),
        api.get('/bubbles'),
      ]);
      setCatalog(c.data.items || []);
      setMine(m.data.cosmetics || []);
      setFoundersPass(!!m.data.founders_pass);
      setBubbles(b.data.bubbles || []);
    } catch (e) {
      toast.error('could not open the shop');
    }
  };
  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  const startCheckout = async (item, bubble_id = null) => {
    setBuying(item.id);
    try {
      const { data } = await api.post('/shop/buy', {
        item_id: item.id,
        bubble_id,
        origin_url: window.location.origin + '/shop',
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'checkout failed');
      setBuying(null);
    }
  };

  const buyFoundersPass = async () => {
    setBuying('founders_pass');
    try {
      const { data } = await api.post('/shop/founders-pass', {
        origin_url: window.location.origin + '/shop',
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not start checkout');
      setBuying(null);
    }
  };

  const handleBuy = (item) => {
    // Bubble-scoped cosmetics need a bubble pick first
    if (['whisper', 'eyes', 'mood_burst'].includes(item.kind)) {
      if ((bubbles || []).length === 0) {
        toast.error('breathe a bubble first.');
        return;
      }
      if (bubbles.length === 1) {
        startCheckout(item, bubbles[0].id);
        return;
      }
      setPickBubble({ open: true, item });
      return;
    }
    startCheckout(item);
  };

  const applyOwned = async (inst, bubble_id) => {
    try {
      await api.post(`/bubbles/${bubble_id}/cosmetics/apply?instance_id=${inst.instance_id}`);
      toast.success('decoration applied ✦');
      nav(`/bubble/${bubble_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not apply');
    }
  };

  const grouped = ORDER.map((tier) => ({
    tier,
    items: catalog.filter((i) => i.tier === tier),
  }));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 pb-32" data-testid="shop-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.4em] text-bx-cyan">decorate a soul</div>
        <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tighter mt-2">
          Adorn
        </h1>
        <p className="text-white/60 mt-3 text-sm sm:text-base max-w-2xl">
          Nothing here makes a bubble stronger. Everything makes one
          <em className="text-white/80"> unmistakably theirs</em>. Mood, voice,
          eyes, palette, the night they were born.
        </p>
      </div>

      {/* Founders Pass — top of stack */}
      <FoundersPassCard owned={foundersPass} onBuy={buyFoundersPass} buying={buying === 'founders_pass'} />

      {grouped.map(({ tier, items }) => {
        if (!items.length) return null;
        const meta = TIER_META[tier];
        const Icon = meta.icon;
        return (
          <section key={tier} className="mt-12" data-testid={`shop-tier-${tier}`}>
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className={`flex items-center gap-2 ${meta.color}`}>
                  <Icon size={16} weight="fill" />
                  <h2 className="font-display text-xl sm:text-2xl font-black">{meta.label}</h2>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">{meta.sub}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {items.map((item) => {
                const ownedInst = mine.find((c) => c.item_id === item.id);
                return (
                  <motion.div
                    key={item.id}
                    data-testid={`shop-item-${item.id}`}
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="glass rounded-2xl p-4 flex flex-col items-center"
                  >
                    <Preview item={item} />
                    <div className="mt-3 font-display font-bold text-center">{item.name}</div>
                    <div className="text-[11px] italic text-white/55 text-center mt-1 min-h-[2.4em]">"{item.tagline}"</div>
                    <div className="mt-3 flex items-center gap-2">
                      <div className="font-mono text-bx-volt text-sm font-bold">${item.price_usd}</div>
                      {ownedInst ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-bx-cyan font-bold">
                          <CheckCircle size={12} weight="fill" /> owned
                        </span>
                      ) : null}
                    </div>
                    {ownedInst ? (
                      <button
                        data-testid={`apply-${item.id}`}
                        onClick={() => {
                          if (bubbles.length === 1) applyOwned(ownedInst, bubbles[0].id);
                          else setPickBubble({ open: true, item, ownedInst });
                        }}
                        className="mt-3 px-4 py-2 rounded-full glass-hi text-xs font-bold hover:bg-white/10 transition w-full">
                        Apply
                      </button>
                    ) : (
                      <button
                        data-testid={`buy-${item.id}`}
                        disabled={buying === item.id}
                        onClick={() => handleBuy(item)}
                        className="mt-3 px-4 py-2 rounded-full btn-volt text-xs font-bold w-full disabled:opacity-50">
                        {buying === item.id ? 'opening…' : 'Adorn'}
                      </button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </section>
        );
      })}

      {/* Bubble picker modal */}
      {pickBubble.open && (
        <div className="fixed inset-0 z-[300] grid place-items-center bg-black/85 backdrop-blur px-4">
          <div className="glass rounded-3xl p-6 w-full max-w-md">
            <div className="text-[10px] uppercase tracking-widest text-bx-cyan mb-2">choose a bubble</div>
            <div className="font-display text-2xl font-black">
              {pickBubble.ownedInst ? 'Apply' : 'Adorn'} · {pickBubble.item.name}
            </div>
            <div className="mt-4 max-h-72 overflow-y-auto space-y-2">
              {bubbles.map((b) => (
                <button
                  key={b.id}
                  data-testid={`pick-bubble-${b.id}`}
                  onClick={() => {
                    if (pickBubble.ownedInst) applyOwned(pickBubble.ownedInst, b.id);
                    else startCheckout(pickBubble.item, b.id);
                    setPickBubble({ open: false, item: null });
                  }}
                  className="w-full flex items-center gap-3 p-3 rounded-2xl glass-hi hover:bg-white/10 text-left">
                  <div className="h-10 w-10 rounded-full overflow-hidden bg-white/5 ring-1 ring-white/15">
                    {b.image_url || b.image_b64 ? (
                      <img alt={b.name} src={b.image_url || `data:image/png;base64,${b.image_b64}`} className="h-full w-full object-cover" />
                    ) : null}
                  </div>
                  <div className="flex-1">
                    <div className="font-bold">{b.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/60">L{b.level || 1}</div>
                  </div>
                </button>
              ))}
            </div>
            <button
              onClick={() => setPickBubble({ open: false, item: null })}
              className="mt-4 w-full px-4 py-2 rounded-full glass-hi text-xs uppercase tracking-widest font-bold hover:bg-white/10">
              cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const FoundersPassCard = ({ owned, onBuy, buying }) => {
  return (
    <div className="grad-border p-6 mt-2" data-testid="founders-pass-card">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-bx-gold flex items-center gap-1">
            <Crown size={12} weight="fill" /> founders pass · $35 · lifetime
          </div>
          <div className="font-display text-2xl sm:text-3xl font-black mt-1">
            {owned ? 'Your flame is already burning.' : 'Be on the first page.'}
          </div>
          <p className="text-white/65 mt-2 text-sm max-w-xl">
            A permanent flame on your name, an Aurora weather you carry everywhere,
            +50 ✦ Resonance the moment it lands, and the ability to forge a
            <em className="text-bx-gold"> Founder-born</em> bubble origin.
          </p>
        </div>
        {owned ? (
          <div className="inline-flex items-center gap-2 px-4 py-3 rounded-full glass-hi text-bx-gold text-xs font-bold uppercase tracking-widest">
            <Crown size={14} weight="fill" className="founders-flame" /> Lit
          </div>
        ) : (
          <button
            onClick={onBuy}
            disabled={buying}
            data-testid="buy-founders-pass"
            className="btn-volt px-6 py-3 rounded-full text-sm font-bold disabled:opacity-50">
            {buying ? 'opening…' : 'Light the flame · $35'}
          </button>
        )}
      </div>
    </div>
  );
};

export default Shop;
